import { useState, useContext } from "react";
import { Form, Input, Button, Upload, Image, Typography, Card, Breadcrumb, Select, DatePicker } from "antd";
import { UploadOutlined, FilePdfOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { Title, Paragraph } = Typography;
const { Option } = Select;

export default function EditAnnouncement({ editData, onBack }) {
    const { authData, backUrl } = useContext(AuthContext);

    const [removeExistingFile, setRemoveExistingFile] = useState(false);

    const [data, setData] = useState({
        announcement_file: null,
        content: editData?.content || "",
        title: editData?.title || "",
        status: editData?.status || "APPROVED",
        display_type: editData?.display_type || "STANDARD",
        start_date: editData?.start_date || "",
        end_date: editData?.end_date || "",
    });

    const isImageFile = (fileUrl) => {
        if (!fileUrl) return false;
        return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileUrl);
    };

    const handleFileChange = ({ fileList }) => {
        if (fileList && fileList.length > 0) {
            setData({ ...data, announcement_file: fileList[0].originFileObj });
        } else {
            setData({ ...data, announcement_file: null });
        }
    };

    // Submit form
    const handleSubmit = async () => {
        // Validate dates if FLEXIBLE
        if (data.display_type === "FLEXIBLE") {
            if (!data.start_date || !data.end_date) {
                toast.error("Please select both start and end dates.");
                return;
            }
            if (dayjs(data.end_date).isBefore(dayjs(data.start_date))) {
                toast.error("End date cannot be before start date.");
                return;
            }
        }

        try {
            const formData = new FormData();
            formData.append("title", data.title);
            formData.append("content", data.content);
            formData.append("display_type", data.display_type);

            if (data.display_type === "FLEXIBLE") {
                formData.append("start_date", data.start_date);
                formData.append("end_date", data.end_date);
            }

            if (data.announcement_file) {
                formData.append("announcement_file", data.announcement_file);
            } else if (removeExistingFile) {
                formData.append("remove_announcement_file", "true"); // notify backend to clear it
            }

            const res = await axios.put(
                `${backUrl}/api/hr/announcements/${editData.id}/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${authData.access}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            toast.success("Announcement updated successfully!");
            console.log("Updated:", res.data);

        } catch (err) {
            console.error("Error updating announcement:", err);
            toast.error("Failed to update announcement. Please try again.");
        }
    };

    return (
        <div style={{ maxWidth: 700, margin: "0 auto", padding: "20px" }}>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

            {/* Breadcrumb */}
            <Breadcrumb
                style={{ marginBottom: 16 }}
                items={[
                    {
                        title: (
                            <span
                                style={{ cursor: "pointer", color: "#0e497b" }}
                                onClick={onBack}
                            >
                                <ArrowLeftOutlined /> Back
                            </span>
                        ),
                    },
                ]}
            />

            <Card
                style={{
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
            >
                <Title level={4} style={{ textAlign: "center", marginBottom: 20, color: "#0e497b" }}>
                    Edit Announcement
                </Title>

                <Form layout="vertical" onFinish={handleSubmit}>
                    {/* Title */}
                    <Form.Item label="Title" required>
                        <Input
                            value={data.title}
                            onChange={(e) => setData({ ...data, title: e.target.value })}
                            placeholder="Enter announcement title"
                        />
                    </Form.Item>

                    {/* Display Type */}
                    <Form.Item label="Display Type" required>
                        <Select
                            value={data.display_type}
                            onChange={(value) =>
                                setData({ ...data, display_type: value, start_date: "", end_date: "" })
                            }
                        >
                            <Option value="STANDARD">Standard</Option>
                            <Option value="FLEXIBLE">Flexible</Option>
                        </Select>
                    </Form.Item>

                    {/* Start & End Date — only if FLEXIBLE */}
                    {data.display_type === "FLEXIBLE" && (
                        <Form.Item label="Date Range" required>
                            <div style={{ display: "flex", gap: "12px" }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>
                                        Start Date
                                    </label>
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        value={data.start_date ? dayjs(data.start_date) : null}
                                        onChange={(_, dateStr) =>
                                            setData({ ...data, start_date: dateStr })
                                        }
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 4 }}>
                                        End Date
                                    </label>
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        value={data.end_date ? dayjs(data.end_date) : null}
                                        disabledDate={(current) =>
                                            data.start_date
                                                ? current && current.isBefore(dayjs(data.start_date), "day")
                                                : false
                                        }
                                        onChange={(_, dateStr) =>
                                            setData({ ...data, end_date: dateStr })
                                        }
                                    />
                                </div>
                            </div>
                        </Form.Item>
                    )}

                    {/* Content */}
                    <Form.Item label="Content" required>
                        <Input.TextArea
                            rows={4}
                            value={data.content}
                            onChange={(e) => setData({ ...data, content: e.target.value })}
                            placeholder="Enter announcement content"
                        />
                    </Form.Item>

                    {/* Existing File Preview */}
                    {editData?.announcement_file && !removeExistingFile && (
                        <Form.Item label="Current File">
                            {isImageFile(editData.announcement_file) ? (
                                <div>
                                    <Image
                                        src={editData.announcement_file}
                                        alt="Current Announcement"
                                        style={{ borderRadius: 8, maxHeight: 200, display: "block", marginBottom: 8 }}
                                    />
                                    <Button danger size="small" onClick={() => setRemoveExistingFile(true)}>
                                        Remove File
                                    </Button>
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "10px 16px",
                                        borderRadius: "8px",
                                        background: "#fff1f0",
                                        color: "#cf1322",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <FilePdfOutlined style={{ fontSize: 28, marginRight: 8, color: "red" }} />
                                        <a
                                            href={editData.announcement_file}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#cf1322" }}
                                        >
                                            View Current File
                                        </a>
                                    </div>
                                    <Button danger size="small" onClick={() => setRemoveExistingFile(true)}>
                                        Remove
                                    </Button>
                                </div>
                            )}
                        </Form.Item>
                    )}

                    {/* Show undo option after removal */}
                    {editData?.announcement_file && removeExistingFile && (
                        <Form.Item label="Current File">
                            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#999" }}>
                                <span>File will be removed on save.</span>
                                <Button size="small" onClick={() => setRemoveExistingFile(false)}>Undo</Button>
                            </div>
                        </Form.Item>
                    )}

                    {/* Replace / Upload File */}
                    <Form.Item label={editData?.announcement_file ? "Replace File" : "Upload File"}>
                        <Upload
                            beforeUpload={() => false}
                            maxCount={1}
                            onChange={handleFileChange}
                            showUploadList={false}
                        >
                            <Button icon={<UploadOutlined />}>
                                {editData?.announcement_file ? "Choose New File" : "Upload File"}
                            </Button>
                        </Upload>

                        {data.announcement_file && (
                            <Paragraph type="secondary" style={{ marginTop: 8 }}>
                                Selected: {data.announcement_file.name}
                            </Paragraph>
                        )}
                    </Form.Item>

                    {/* Submit */}
                    <Form.Item>
                        <center>
                            <Button
                                style={{ color: "white", backgroundColor: "#0e497b", width: "140px" }}
                                htmlType="submit"
                            >
                                Save Changes
                            </Button>
                        </center>
                    </Form.Item>
                </Form>
            </Card>
        </div >
    );
}